import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';

import {
  buildContactPayload,
  ContactFormPayload,
  ContactFormState,
  emptyContactForm,
} from './addContactUtils';
import { labelSx } from './LocationHelpers';

const triggerSx = {
  fontSize: '11px',
  fontWeight: 600,
  px: 1,
  py: 0.25,
  cursor: 'pointer',
  borderRadius: '4px',
  bgcolor: 'transparent',
  border: '1px dashed',
  borderColor: 'v4.border',
  color: 'v4.accent',
  fontFamily: 'inherit',
  '&:hover': { borderColor: 'v4.accent', bgcolor: 'v4.tableHover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'v4.accent', outlineOffset: 1 },
};

const fieldInputSx = {
  fontSize: '12px',
  border: '1px solid',
  borderColor: 'v4.borderDark',
  borderRadius: 1,
  px: 1,
  py: 0.5,
  width: '100%',
};

const cancelBtnSx = {
  fontSize: '11px',
  px: 1.5,
  py: 0.5,
  border: 'none',
  bgcolor: 'transparent',
  cursor: 'pointer',
  color: 'text.secondary',
  fontFamily: 'inherit',
  borderRadius: '4px',
  '&:hover': { color: 'text.primary' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'v4.accent', outlineOffset: 1 },
};

const saveBtnSx = {
  fontSize: '11px',
  fontWeight: 600,
  px: 2,
  py: 0.5,
  border: 'none',
  borderRadius: '4px',
  bgcolor: 'v4.accent',
  color: 'common.white',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { opacity: 0.9 },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'v4.accent', outlineOffset: 2 },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
};

export interface AddContactFormProps {
  /** Called with the new-contact payload when the user clicks Save. */
  onSaveContact: (contact: ContactFormPayload) => void;
}

/**
 * Inline "Add Contact" popover used on the LocationPanel. Mirrors the old-CMS2
 * Service Contact form (name, organisation, phone1/2, email, notes) and emits
 * a service-scoped contact payload when saved.
 */
export const AddContactForm: React.FC<AddContactFormProps> = ({ onSaveContact }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ContactFormState>(emptyContactForm);

  const update = (key: keyof ContactFormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleOpen = () => {
    setForm(emptyContactForm);
    setOpen(true);
  };

  // Explicit close (Cancel / Save) — restore focus to the trigger so keyboard
  // users don't lose their place. Click-away uses handleClickAway instead so it
  // doesn't yank focus from wherever the user just clicked.
  const closeAndResetForm = () => {
    setOpen(false);
    setForm(emptyContactForm);
    anchorRef.current?.focus();
  };

  const handleCancel = () => closeAndResetForm();

  const handleClickAway = () => {
    setOpen(false);
    setForm(emptyContactForm);
  };

  const canSave = form.contactName.trim().length > 0;

  const handleSave = () => {
    onSaveContact(buildContactPayload(form));
    closeAndResetForm();
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
      <Box
        component="button"
        type="button"
        ref={anchorRef}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleOpen}
        sx={triggerSx}
      >
        Add Contact
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={8}
            sx={{
              width: 360,
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
            }}
          >
            <Typography sx={{ ...labelSx, mb: 1 }}>Add Service Contact</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <InputBase
                placeholder="Name *"
                value={form.contactName}
                onChange={(e) => update('contactName', e.target.value)}
                inputProps={{ 'aria-label': 'Contact name' }}
                sx={fieldInputSx}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a popper is an expected focus target
                autoFocus
              />
              <InputBase
                placeholder="Organisation"
                value={form.organization}
                onChange={(e) => update('organization', e.target.value)}
                inputProps={{ 'aria-label': 'Organisation' }}
                sx={fieldInputSx}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                <InputBase
                  placeholder="Phone 1"
                  value={form.phone1}
                  onChange={(e) => update('phone1', e.target.value)}
                  inputProps={{ 'aria-label': 'Phone 1' }}
                  sx={fieldInputSx}
                />
                <InputBase
                  placeholder="Phone 2"
                  value={form.phone2}
                  onChange={(e) => update('phone2', e.target.value)}
                  inputProps={{ 'aria-label': 'Phone 2' }}
                  sx={fieldInputSx}
                />
              </Box>
              <InputBase
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                inputProps={{ 'aria-label': 'Email' }}
                sx={fieldInputSx}
              />
              <InputBase
                placeholder="Notes"
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
                inputProps={{ 'aria-label': 'Notes' }}
                multiline
                minRows={2}
                maxRows={4}
                sx={fieldInputSx}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
              <Box component="button" type="button" onClick={handleCancel} sx={cancelBtnSx}>
                Cancel
              </Box>
              <Box
                component="button"
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                sx={saveBtnSx}
              >
                Save Contact
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
